import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Combined template + first rule-set creator, used from the assessment editor
 * so Sam can "create a new template and attach it to this level" without
 * leaving the drawer. Both underlying endpoints (createTemplate,
 * createRuleSet) are the same ones TemplatesScreen uses — same validation,
 * same server-side satisfiability check — the only new thing here is the
 * combined form.
 *
 * On save the newly-minted { template_id, rule_set_id } is handed back to
 * the caller (usually appended to a level's link list in the drawer state).
 *
 * The rules area is a visual builder. The variable dropdowns inside it
 * derive from the Prompt + Answer rule fields live, so adding a new
 * placeholder in the prompt immediately makes it available in the
 * builder's selects.
 */
import React, { useMemo, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Typography } from 'antd';
import { createRuleSet, createTemplate } from '../../api';
import { ApiError } from '../../api/client';
import { notifySuccess } from '../errors';
import { RuleBuilder, serializeRow, variablesFromTemplate, } from '../content/ruleBuilder';
import { TemplateImageField } from '../content/TemplateImageField';
import { AnswerFieldFrame, PromptFieldFrame, useTemplateVariableChips, } from '../content/VariableChips';
import { PromptDirectionToggle, usePromptDirection } from '../content/promptDirection';
const { Text } = Typography;
export function TemplateWithRuleSetModal({ open, onClose, onCreated }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const { promptRef, answerRef, promptChips, answerChips } = useTemplateVariableChips(form);
    const { dir: promptDir, setDir: setPromptDir } = usePromptDirection(form, 'prompt', open);
    const [rows, setRows] = useState([]);
    const [serverErrors, setServerErrors] = useState({});
    const [showUnsatisfiableWarning, setShowUnsatisfiableWarning] = useState(false);
    // Watch the prompt + answer-rule fields so the variable list the
    // builder uses stays in sync with what Sam is typing.
    const promptValue = Form.useWatch('prompt', form) ?? '';
    const answerValue = Form.useWatch('answer_rule', form) ?? '';
    const variables = useMemo(() => variablesFromTemplate(promptValue, answerValue), [promptValue, answerValue]);
    // Clear derived state when the modal closes so a reopen starts fresh.
    React.useEffect(() => {
        if (!open) {
            setRows([]);
            setServerErrors({});
        }
    }, [open]);
    const doSave = async (force) => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return 'other';
        }
        const rules = rows
            .map((row) => serializeRow(row).trim())
            .filter((s) => s.length > 0)
            .map((expression) => ({ expression }));
        if (rules.length === 0) {
            // A rule set with no rules can't be dry-run and would leave Sam
            // with an unusable link; block the save with an inline message.
            setServerErrors({ 0: 'נדרש לפחות חוק אחד' });
            return 'other';
        }
        try {
            const t = await createTemplate({
                prompt: v.prompt.trim(),
                answer_rule: v.answer_rule.trim(),
                image_url: v.image_url?.trim() || null,
            });
            try {
                const rs = await createRuleSet({
                    question_template_id: t.id,
                    name: v.rule_set_name.trim(),
                    is_default: !!v.is_default,
                    rules,
                    force,
                });
                notifySuccess('תבנית חדשה נוצרה עם סט חוקים');
                setServerErrors({});
                onCreated({ template_id: t.id, rule_set_id: rs.id });
                form.resetFields();
                setRows([]);
                return 'ok';
            }
            catch (rsErr) {
                if (rsErr instanceof ApiError) {
                    if (rsErr.code === 'rule_syntax') {
                        const errs = rsErr.details.line_errors ?? [];
                        const map = {};
                        for (const e of errs)
                            map[e.index] = `${e.expression}: ${e.message}`;
                        setServerErrors(map);
                        form.setFieldsValue({ __existing_template_id: t.id });
                        return 'syntax';
                    }
                    if (rsErr.code === 'unsatisfiable_rules') {
                        form.setFieldsValue({ __existing_template_id: t.id });
                        return 'unsatisfiable';
                    }
                }
                return 'other';
            }
        }
        catch {
            return 'other';
        }
    };
    const submit = async () => {
        setSubmitting(true);
        try {
            const r = await doSave(false);
            if (r === 'unsatisfiable')
                setShowUnsatisfiableWarning(true);
        }
        finally {
            setSubmitting(false);
        }
    };
    const confirmForceSave = async () => {
        setShowUnsatisfiableWarning(false);
        setSubmitting(true);
        try {
            await doSave(true);
        }
        finally {
            setSubmitting(false);
        }
    };
    const onRowsChange = (next) => {
        setRows(next);
        if (Object.keys(serverErrors).length > 0)
            setServerErrors({});
    };
    return (_jsxs(Modal, { title: "\u05E6\u05D5\u05E8 \u05EA\u05D1\u05E0\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4 + \u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD", open: open, onCancel: onClose, onOk: submit, okText: "\u05E6\u05D5\u05E8 \u05D5\u05D4\u05E6\u05DE\u05D3", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, width: 720, destroyOnHidden: true, children: [_jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Alert, { type: "info", showIcon: true, message: "\u05EA\u05D1\u05E0\u05D9\u05EA + \u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD \u05E8\u05D0\u05E9\u05D5\u05DF \u05E0\u05E9\u05DE\u05E8\u05D9\u05DD \u05D9\u05D7\u05D3; \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4, \u05D4\u05E9\u05D9\u05DC\u05D5\u05D1 \u05DE\u05D5\u05E6\u05DE\u05D3 \u05DC\u05E8\u05DE\u05D4 \u05E9\u05D0\u05EA\u05D4 \u05E2\u05D5\u05E8\u05DA.", style: { marginBottom: 16 } }), _jsx(Typography.Title, { level: 5, style: { marginTop: 0 }, children: "\u05EA\u05D1\u05E0\u05D9\u05EA" }), _jsx(Form.Item, { name: "prompt", label: "\u05E0\u05D5\u05E1\u05D7 \u05D4\u05E9\u05D0\u05DC\u05D4", rules: [{ required: true, message: 'נוסח השאלה נדרש' }], tooltip: "\u05D8\u05E7\u05E1\u05D8 \u05D4\u05E9\u05D0\u05DC\u05D4. \u05DE\u05E9\u05EA\u05E0\u05D9\u05DD \u05E0\u05DB\u05EA\u05D1\u05D9\u05DD \u05D1\u05E1\u05D5\u05D2\u05E8\u05D9\u05D9\u05DD \u05DE\u05E1\u05D5\u05DC\u05E1\u05DC\u05D9\u05DD, \u05DC\u05DE\u05E9\u05DC: \u05DB\u05DE\u05D4 \u05D6\u05D4 {a} + {b}?", children: _jsx(PromptFieldFrame, { textAreaRef: promptRef, dir: promptDir, rows: 2, chipsEl: promptChips, toggleEl: _jsx(PromptDirectionToggle, { dir: promptDir, onChange: setPromptDir }) }) }), _jsx(Form.Item, { name: "answer_rule", label: "\u05E0\u05D5\u05E1\u05D7\u05EA \u05D4\u05EA\u05E9\u05D5\u05D1\u05D4", rules: [{ required: true, message: 'נוסחת התשובה נדרשת' }], tooltip: "\u05D4\u05D1\u05D9\u05D8\u05D5\u05D9 \u05E9\u05DE\u05D7\u05E9\u05D1 \u05D0\u05EA \u05D4\u05EA\u05E9\u05D5\u05D1\u05D4 \u05D4\u05E0\u05DB\u05D5\u05E0\u05D4, \u05DC\u05DE\u05E9\u05DC: a + b", children: _jsx(AnswerFieldFrame, { inputRef: answerRef, placeholder: "a + b", chipsEl: answerChips }) }), _jsx(Form.Item, { name: "image_url", label: "\u05EA\u05DE\u05D5\u05E0\u05D4 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)", children: _jsx(TemplateImageField, {}) }), _jsx(Typography.Title, { level: 5, style: { marginTop: 20 }, children: "\u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD \u05E8\u05D0\u05E9\u05D5\u05DF" }), _jsx(Form.Item, { name: "rule_set_name", label: "\u05E9\u05DD \u05D4\u05E1\u05D8", rules: [{ required: true, message: 'שם נדרש' }], tooltip: "\u05EA\u05D5\u05D5\u05D9\u05EA \u05E7\u05E6\u05E8\u05D4, \u05DC\u05DE\u05E9\u05DC: \u05D1\u05E1\u05D9\u05E1\u05D9 / \u05DE\u05EA\u05E7\u05D3\u05DD", children: _jsx(Input, { placeholder: "\u05DC\u05DE\u05E9\u05DC: \u05D1\u05E1\u05D9\u05E1\u05D9" }) }), _jsxs("div", { style: { marginBottom: 16 }, children: [_jsx(Text, { style: { fontWeight: 500, display: 'block', marginBottom: 8 }, children: "\u05D7\u05D5\u05E7\u05D9\u05DD" }), _jsx(Text, { type: "secondary", style: { fontSize: 12, display: 'block', marginBottom: 8 }, children: "\u05DB\u05DC \u05D7\u05D5\u05E7 \u05D4\u05D5\u05D0 \u05EA\u05E0\u05D0\u05D9 \u05E0\u05E4\u05E8\u05D3. \u05D4\u05EA\u05E0\u05D0\u05D9\u05DD \u05DE\u05E6\u05D8\u05E8\u05E4\u05D9\u05DD \u05D6\u05D4 \u05DC\u05D6\u05D4 (\u05D5\u05D2\u05DD)." }), _jsx(RuleBuilder, { rows: rows, onChange: onRowsChange, variables: variables, serverErrors: serverErrors })] }), _jsx(Form.Item, { name: "is_default", valuePropName: "checked", tooltip: "\u05E1\u05D9\u05DE\u05D5\u05DF \u05D6\u05D4 \u05E7\u05D5\u05D1\u05E2 \u05E9\u05D4\u05E1\u05D8 \u05D9\u05D9\u05D1\u05D7\u05E8 \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05DB\u05E9\u05DE\u05E6\u05DE\u05D9\u05D3\u05D9\u05DD \u05D0\u05EA \u05D4\u05EA\u05D1\u05E0\u05D9\u05EA \u05DC\u05D1\u05D5\u05D7\u05DF. \u05D0\u05D9\u05DF \u05DC\u05DB\u05DA \u05D4\u05E9\u05E4\u05E2\u05D4 \u05E2\u05DC \u05D4\u05D4\u05EA\u05E0\u05D4\u05D2\u05D5\u05EA \u05D1\u05D1\u05D5\u05D7\u05DF \u05E2\u05E6\u05DE\u05D5.", children: _jsx(Checkbox, { children: "\u05E1\u05D8 \u05D1\u05E8\u05D9\u05E8\u05EA \u05D4\u05DE\u05D7\u05D3\u05DC \u05E9\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05EA" }) })] }), _jsxs(Modal, { open: showUnsatisfiableWarning, title: "\u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D4\u05E1\u05D8 \u05D1\u05DC\u05EA\u05D9-\u05D0\u05E4\u05E9\u05E8\u05D9", okText: "\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA", cancelText: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", okButtonProps: { danger: true }, onOk: confirmForceSave, onCancel: () => setShowUnsatisfiableWarning(false), confirmLoading: submitting, children: [_jsx("p", { children: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05E2\u05E8\u05DB\u05D9\u05DD \u05D4\u05E2\u05D5\u05DE\u05D3\u05D9\u05DD \u05D1\u05D7\u05D5\u05E7\u05D9\u05DD \u05D1\u05D3\u05D2\u05D9\u05DE\u05D4." }), _jsx("p", { children: "\u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D4\u05E1\u05D8 \u05D1\u05DC\u05EA\u05D9-\u05D0\u05E4\u05E9\u05E8\u05D9 (\u05DB\u05DE\u05D5 a > b \u05D9\u05D7\u05D3 \u05E2\u05DD b > a) \u05D0\u05D5 \u05E9\u05D4\u05E1\u05D1\u05D9\u05E8\u05D5\u05EA \u05DC\u05DE\u05E6\u05D5\u05D0 \u05E2\u05E8\u05DA \u05EA\u05E7\u05E3 \u05E0\u05DE\u05D5\u05DB\u05D4 \u05DE\u05D3\u05D9." }), _jsx("p", { children: "\u05DC\u05E9\u05DE\u05D5\u05E8 \u05D1\u05DB\u05DC \u05D6\u05D0\u05EA?" })] })] }));
}
